import {useNavigate} from 'react-router-dom';
import {formatDistanceToNow} from 'date-fns';
import {Ticket as TicketIcon, ExternalLink} from 'lucide-react';
import {trpc} from '@common-web/lib/trpc';
import {STATUS_LABELS, STATUS_BADGE_CLASS} from '@common-web/components/tickets/constants';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Badge} from '@common-web/components/ui/badge';
import {DataTable, useDataTable, type Column} from '@common-web/components/data-table';

const CB_VISIBLE_STATUSES = [
  'SENT_TO_CLEVER_BENEFITS',
  'SENT_TO_ENGINEERING',
  'BACKLOG',
  'UPCOMING',
  'CURRENT_SPRINT',
  'IN_PROGRESS',
  'COMPLETED',
] as const;
type CbStatus = (typeof CB_VISIBLE_STATUSES)[number];
type StatusFilter = CbStatus | 'ALL';

export function InternalTickets() {
  const navigate = useNavigate();

  const table = useDataTable<{status: string}>({defaultFilters: {status: ''}});

  const listQuery = trpc.internal.tickets.list.useQuery({
    status: (table.filterValues.status || undefined) as CbStatus | undefined,
    limit: 100,
  });

  const columns: Column<Ticket>[] = [
    {
      id: 'ticketNumber',
      header: 'Ticket',
      render: (row) => <span className='font-mono text-xs'>{row.ticketNumber}</span>,
    },
    {
      id: 'tenant',
      header: 'Tenant',
      render: (row) => row.client.name,
    },
    {
      id: 'title',
      header: 'Title',
      className: 'max-w-md truncate',
      render: (row) => row.title,
    },
    {
      id: 'status',
      header: 'Status',
      render: (row) => (
        <Badge className={STATUS_BADGE_CLASS[row.status as CbStatus]}>
          {STATUS_LABELS[row.status as CbStatus] ?? row.status}
        </Badge>
      ),
    },
    {
      id: 'submitter',
      header: 'Submitter',
      render: (row) => row.submitter?.name ?? row.submitter?.email ?? '—',
    },
    {
      id: 'asana',
      header: 'Asana',
      render: (row) =>
        row.asanaTaskUrl ? (
          <a
            href={row.asanaTaskUrl}
            target='_blank'
            rel='noreferrer'
            onClick={(e) => e.stopPropagation()}
            className='text-brand-blue inline-flex items-center gap-1 text-xs'
          >
            Open <ExternalLink className='h-3 w-3' />
          </a>
        ) : (
          <span className='text-xs text-muted-foreground'>—</span>
        ),
    },
    {
      id: 'updatedAt',
      header: 'Updated',
      className: 'text-sm text-muted-foreground',
      render: (row) => formatDistanceToNow(new Date(row.updatedAt), {addSuffix: true}),
    },
  ];

  return (
    <div className='p-6 space-y-4'>
      <div className='flex items-center justify-between'>
        <h1 className='text-2xl font-semibold flex items-center gap-2'>
          <TicketIcon className='h-6 w-6 text-brand-blue' />
          Development Requests
        </h1>
      </div>

      <DataTable<Ticket, {status: string}>
        table={table}
        columns={columns}
        data={listQuery.data}
        loading={listQuery.isLoading}
        error={listQuery.error}
        emptyMessage='No tickets in the Clever Benefits queue.'
        title='Tickets'
        entity={{singular: 'ticket', plural: 'tickets'}}
        onRowClick={(row) => navigate(row.id)}
        filters={[
          {
            key: 'status',
            label: 'Forwarded',
            type: 'select',
            placeholder: 'All forwarded',
            options: CB_VISIBLE_STATUSES.map((k) => ({
              value: k,
              label: STATUS_LABELS[k],
            })),
          },
        ]}
      />
    </div>
  );
}
